// Scenario 3: Booked - Region Only
// User needs to select their area within the known region
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { islandScopedDocumentFilter, sanityFetch } from '../../lib/sanity';
import { useRegionsData } from '../../hooks/useRegionsData';
import RegionsMapLite from '../maps/RegionsMapLite';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase';
import { getHomebaseIslandKey } from '../../utils/homebaseGeography';

const BookedRegionScenario = ({ data, interviewId }) => {
  const navigate = useNavigate();
  const [selectedArea, setSelectedArea] = useState(null);
  const [regionAreas, setRegionAreas] = useState([]);
  const [isUpdating, setIsUpdating] = useState(false);
  
  // Get master list of all areas for validation
  const { areasData: allAreas } = useRegionsData();

  // Extract data before early return
  const islandKey = getHomebaseIslandKey(data);
  const region = data?.region;
  

  // Fetch all areas within the region
  useEffect(() => {
    const fetchRegionAreas = async () => {
      if (!region) return;
      
      try {
        // First, get the region data to find its slug
        const regionQuery = `*[_type == "region" && ${islandScopedDocumentFilter()} && title == $regionTitle][0]{
          _id,
          title,
          slug
        }`;
        
        const regionData = await sanityFetch(regionQuery, { regionTitle: region });
        
        if (regionData) {
          // Now fetch all areas in this region
          const areasQuery = `*[_type == "area" && ${islandScopedDocumentFilter()} && region._ref == $regionId]{
            _id,
            title,
            oneLiner,
            heroImage{asset->{url}, alt},
            slug,
            region->{
              _id,
              title,
              slug
            }
          } | order(title asc)`;
          
          const areas = await sanityFetch(areasQuery, { regionId: regionData._id });
          setRegionAreas(areas);
        }
      } catch (error) {
        console.error('[BookedRegionScenario] Error fetching region areas:', error);
      }
    };

    fetchRegionAreas();
  }, [region]);


  // Early return check after hooks
  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-white/70">No homebase data available for this scenario.</p>
      </div>
    );
  }

  const handleEditInterview = () => {
    navigate(`/interview/review/${interviewId}`);
  };

  const handleConfirmHomebase = async () => {
    if (!selectedArea || !region) return;
    
    setIsUpdating(true);
    
    try {
      // Find the selected area data
      const selectedAreaData = regionAreas.find(a => a.slug.current === selectedArea);
      if (!selectedAreaData) {
        throw new Error('Selected area not found');
      }
      
      // Update the interview in Firestore
      const docRef = doc(db, 'interviews', interviewId);
      await updateDoc(docRef, {
        island: islandKey,
        'lodging.bookedDetails.area': selectedAreaData.title,
        'lodging.bookedDetails.region': region,
        selectedArea: selectedAreaData.title,
        selectedRegion: region,
        selectedAreaSlug: selectedAreaData.slug.current,
        selectedRegionSlug: data?.regionSlug || null,
        'metadata.lastUpdated': serverTimestamp(),
        'metadata.homebaseAreaConfirmed': true
      });
      
      // Navigate to homebase with refresh flag to trigger Scenario 2
      navigate(`/homebase/${interviewId}?refresh=true`);
    } catch (error) {
      console.error('[BookedRegionScenario] Error updating interview:', error);
      alert('Failed to save your location. Please try again.');
      setIsUpdating(false);
    }
  };

  const handleAreaSelect = (areaSlug) => {
    setSelectedArea(areaSlug);
  };

  const handleMapAreaClick = (type, areaName) => {
    if (type === 'area') {
      if (!allAreas) {
        return;
      }
      
      // Try multiple matching strategies
      const area = allAreas.find(a => 
        a.title === areaName ||                           // exact match
        a.title.toLowerCase() === areaName.toLowerCase() || // case-insensitive match
        a.slug.current === areaName ||                      // exact slug match
        a.slug.current === areaName.toLowerCase()          // case-insensitive slug match
      );
      
      if (area) {
        setSelectedArea(area.slug.current);
      }
    }
  };

  return (
    <div className="min-h-screen">
      <div className="max-w-6xl mx-auto px-4 pb-8">
        
        {/* Primary CTAs - Primary hidden until area selection */}
        <div className="flex flex-col md:flex-row md:justify-between gap-4 mb-4">
          <button 
            onClick={handleEditInterview}
            className="btn-secondary w-full md:w-72 px-8 py-4 text-lg"
          >
            Edit Interview
          </button>
          {selectedArea && (
            <button 
              onClick={handleConfirmHomebase}
              className="btn-primary-cta w-full md:w-72 px-8 py-4 text-lg"
              disabled={isUpdating}
            >
              {isUpdating 
                ? 'Saving...'
                : `Confirm ${regionAreas.find(a => a.slug.current === selectedArea)?.title || 'Area'} as Homebase`
              }
            </button>
          )}
        </div>

        {/* Header Message */}
        <div className="mb-6">
          <h2 className="text-xl font-semibold mb-2 text-white">
            Tell us where on the {region} you are staying
          </h2>
          <p className="text-white/90">
            We can anchor your trip in the right location once you select your specific area.
          </p>
        </div>

        {/* Map + List Layout (inspired by RegionsGuidePreview) */}
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-6 mb-8">
          {/* Map Column */}
          <div className="relative">
            <div className="h-[400px] lg:h-[500px] rounded-lg overflow-hidden border border-gray-600">
              {/* Only render map when we have all areas data loaded */}
              {allAreas ? (
                <RegionsMapLite
                  islandKey={islandKey}
                  selectable={true}
                  areasSelectable={true}
                  selectedRegion={region}
                  selectedArea={selectedArea ? regionAreas.find(a => a.slug.current === selectedArea)?.title : null}
                  onSelect={handleMapAreaClick}
                  focusedRegionMode={true}
                  className="w-full h-full"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-gray-800 text-white">
                  <p>Loading map...</p>
                </div>
              )}
            </div>
          </div>

          {/* List Column */}
          <div className="lg:w-[380px]">
            <div className="space-y-2">
              {regionAreas.map((area) => (
                <button
                  key={area._id}
                  className={`w-full text-left bg-white/5 border rounded-lg p-3 transition-all duration-200 cursor-pointer group ${
                    selectedArea === area.slug.current
                      ? 'border-green-400 bg-green-400/10'
                      : 'border-gray-600 hover:border-green-400 hover:bg-white/10'
                  }`}
                  onClick={() => handleAreaSelect(area.slug.current)}
                >
                  <div className="flex items-center justify-between mb-1">
                    <h4 className={`font-semibold transition-colors ${
                      selectedArea === area.slug.current
                        ? 'text-white'
                        : 'text-white group-hover:text-green-300'
                    }`}>
                      {area.title}
                    </h4>
                    {selectedArea === area.slug.current && (
                      <span className="text-sm text-green-300">✓</span>
                    )}
                  </div>
                  <p className={`text-sm mt-0 transition-colors ${
                    selectedArea === area.slug.current
                      ? 'text-white/90'
                      : 'text-gray-300 group-hover:text-green-200'
                  }`}>
                    {area.oneLiner}
                  </p>
                </button>
              ))}
            </div>
          </div>
        </div>


        {/* Bottom CTAs - Primary only shown after area selection */}
        <div className="flex flex-col md:flex-row md:justify-between gap-4">
          <button 
            onClick={handleEditInterview}
            className="btn-secondary w-full md:w-72 px-8 py-4 text-lg"
          >
            Edit Interview
          </button>
          {selectedArea && (
            <button 
              onClick={handleConfirmHomebase}
              className="btn-primary-cta w-full md:w-72 px-8 py-4 text-lg"
              disabled={isUpdating}
            >
              {isUpdating 
                ? 'Saving...'
                : `Confirm ${regionAreas.find(a => a.slug.current === selectedArea)?.title || 'Area'} as Homebase`
              }
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default BookedRegionScenario;
